import './Dashboard.scss';

import * as React from 'react';
import * as _ from 'lodash';

import { Label, Spinner, SpinnerSize } from '@fluentui/react';

import { PieChart } from 'react-minimal-pie-chart';
import { VisitStatus } from '../../types';
import { getIconDetails } from '../visit/VisitStatusIcon';
import { DateSelector } from '../../components/core/DateSelector';
import { DocumentTitle } from '../../components/core/DocumentTitle';
import { useEffect } from 'react';
import { ROUTES } from '../../config/routes';
import { useNavigate } from 'react-router-dom';
import { onPageDidMount, onPageWillUnmount } from '../../redux/core';
import { toShortDateString } from '../../util/Date';
import { setMarkets } from '../../redux/ui/actions';
import { useDispatch, useSelector } from 'react-redux';
import { Market } from '../../api/markets.types';
import { RootState } from '../../redux/types';
import { useParams } from 'react-router';

interface RollupData {
  marketCode: string;
  visitStatus: string;
  serviceCode: string;
  cancelReasonId: string;
  count: number;
}

export const Dashboard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { date: paramDate } = useParams<{ date?: string }>();

  const markets = useSelector((state: RootState) => state.markets.markets.data || []);
  const visitsOverview = useSelector((state: RootState) => state.dashboard.visitsOverview);

  useEffect(() => {
    dispatch(onPageDidMount('Dashboard'));

    return () => {
      dispatch(onPageWillUnmount('Dashboard'));
    };
  }, [dispatch]);

  const marketsWithOnsite = [{ code: 'XX', name: 'Onsites', groupCode: 'Enterprise' } as Market].concat(markets);
  const date = paramDate || toShortDateString(new Date());
  const asyncData = visitsOverview[date] || { isFetching: true };
  const loading = asyncData.isFetching;

  return (
    <>
      <DocumentTitle title="Dashboard" />
      <div className="flex flex-row flex-center">
        <div className="page-title">Analytics</div>
        <DateSelector
          showNudgeButtons={true}
          defaultDate={date}
          onChange={(value) => {
            navigate(ROUTES.dashboard, { state: { date: value } });
            dispatch(onPageDidMount('Dashboard'));
          }}
        />
      </div>
      {loading && <Spinner size={SpinnerSize.large} label="Counting visits..." />}
      {!loading && <Overview data={asyncData.data!} />}
      {!loading && <MarketGroups markets={marketsWithOnsite} data={asyncData.data!} />}
    </>
  );
};

const Overview = ({ data }: { data: RollupData[] }) => {
  const pieData = _.compact(
    VisitStatusOrder.map((status) => {
      const statusCount = _.sumBy(data, (d) => (d.visitStatus === status ? d.count : 0));
      if (statusCount === 0) {
        return null;
      }
      return {
        title: status,
        value: statusCount,
        key: status,
        color: getIconDetails(status).color,
      };
    })
  );

  const totalExcludingCanceled = _.sumBy(data, (d) => (d.visitStatus !== VisitStatus.CANCELLED && d.visitStatus !== VisitStatus.FAILED_PREAUTH ? d.count : 0));

  const serviceCodes = _.groupBy(data, (d) => d.serviceCode);

  return (
    <div className="rollup ms-depth-8">
      <div className="rollup-chart">
        <PieChart lineWidth={40} animate={true} data={pieData} />
        <div className="total" data-tid="txt_totalNotCanceled">
          {totalExcludingCanceled}
        </div>
      </div>
      <div className="flex flex-row flex-grow rollup-details">
        <div style={{ flex: 1, marginLeft: 50 }}>
          <Label>Status Breakdown</Label>
          {VisitStatusOrder.map((status, index) => {
            const count = _.sumBy(data, (d) => (d.visitStatus === status ? d.count : 0));
            if (count === 0) {
              return null;
            }
            return (
              <div className="legend" key={index}>
                <div className="block" style={{ backgroundColor: getIconDetails(status).color }} />
                <div className="title" style={{ color: getIconDetails(status).color }}>
                  {count} - {status}
                </div>
              </div>
            );
          })}
        </div>
        <div style={{ flex: 1 }}>
          <Label>Service Breakdown</Label>
          {Object.keys(serviceCodes).map((serviceCode) => {
            const count = _.sumBy(data, (d) =>
              d.visitStatus !== VisitStatus.CANCELLED && d.visitStatus !== VisitStatus.FAILED_PREAUTH && d.serviceCode === serviceCode ? d.count : 0
            );
            if (count === 0) {
              return null;
            }
            return (
              <div className="flex flex-row" key={serviceCode}>
                <div style={{ width: 40, textAlign: 'right', marginRight: 10 }}>{count}</div>
                <div>{serviceCode}</div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const MarketGroups = ({ markets, data }: { markets: Market[]; data: RollupData[] }) => {
  const groups = _.groupBy(markets, 'groupCode');
  return (
    <div className="market-groups">
      {Object.keys(groups).map((groupCode) => (
        <div key={groupCode}>
          <Label className="ms-font-xxl">{groupCode}</Label>
          <MarketSummaries markets={groups[groupCode]} data={data} />
        </div>
      ))}
    </div>
  );
};

const MarketSummaries = ({ markets, data }: { markets: Market[]; data: RollupData[] }) => {
  return (
    <div className="tiles-container">
      {markets.map((m) => (
        <MarketSummary key={m.code} name={m.name} marketCode={m.code} data={data.filter((d) => d.marketCode === m.code)} />
      ))}
    </div>
  );
};

const MarketSummary = ({ name, marketCode, data }: { name: string; marketCode: string; data: RollupData[] }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const onClick = (e: React.MouseEvent) => {
    if (marketCode === 'XX') {
      if (e.metaKey || e.ctrlKey) {
        window.open(ROUTES.onSiteManager, '_blank');
      } else {
        navigate(ROUTES.onSiteManager);
      }
    } else {
      dispatch(setMarkets([marketCode]));
      if (e.metaKey || e.ctrlKey) {
        window.open(ROUTES.visits, '_blank');
      } else {
        navigate(ROUTES.visits);
      }
    }
  };

  const totalExcludingCanceled = _.sumBy(data, (d) => (d.visitStatus !== VisitStatus.CANCELLED && d.visitStatus !== VisitStatus.FAILED_PREAUTH ? d.count : 0));
  const groups = _.groupBy(data, (d) => d.visitStatus);

  return (
    <div className="tile-container" key={marketCode}>
      <button className="link-button tile-main" onClick={onClick}>
        <div className="title-container">
          <div className="title">{marketCode === 'XX' ? name : marketCode}</div>
          <div className="subtitle">{marketCode !== 'XX' && name}</div>
        </div>
        <div className="count">{totalExcludingCanceled}</div>
      </button>
      <div className="tile-details">
        {VisitStatusOrder.map((status, index) => {
          const group = groups[status];
          if (!group) {
            return null;
          }
          const countOfStatus = _.sumBy(group, (d) => d.count);
          if (countOfStatus === 0) {
            return null;
          }
          return (
            <div key={index} style={{ color: getIconDetails(status).color }}>
              {countOfStatus} - {status}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const VisitStatusOrder = [
  VisitStatus.NEW,
  VisitStatus.SEARCHING_DOCTOR,
  VisitStatus.QUEUED,
  VisitStatus.DOCTOR_ACCEPTED,
  VisitStatus.DOCTOR_ASSIGNED,
  VisitStatus.DOCTOR_ENROUTE,
  VisitStatus.DOCTOR_ACKNOWLEDGED,
  VisitStatus.DOCTOR_NOT_FOUND,
  VisitStatus.STARTED,
  VisitStatus.CANCELLED,
  VisitStatus.NOT_AUTHORIZED_VALIDATION,
  VisitStatus.FAILED_PREAUTH,
  VisitStatus.FULLY_PAID,
  VisitStatus.REFUNDED,
];
